import React, { useState, useEffect } from "react";
import { keyframes } from "@emotion/react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import CloseIcon from "@mui/icons-material/Close";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import NavBar from "./NavBar";
import Footer from "./Footer";
import houseMain from "./images/housemain.jpg";
import gutterClearing from "./images/gutterclearing.jpg";
import newRoof from "./images/newroof.jpg";
import siding from "./images/siding1.jpg";
import emergencyRepair from "./images/emergencyrepair.jpg";
import roofVent from "./images/roofvent.jpeg";
import flatRoof from "./images/flatroof.jpg";
import roofInspection from "./images/roofInspection.jpg";
import roofRepair from "./images/roofrepair.jpg";
import gutterCleaning from "./images/guttercleaning.jpg";
import roof1Video from "./videos/roof1.mp4";
import roof2Video from "./videos/roof2.mp4";
import roof3Video from "./videos/roof3.mp4";
import roof4Video from "./videos/roof4.mp4";
import roof5Video from "./videos/roof5.mp4";
import roof6Video from "./videos/roof6.mp4";
import roof7Video from "./videos/roof7.mp4";
import roofStory1 from "./videos/dizaverstory1.mp4";
import roofFullStory1 from "./videos/dizaverfullstory1.mp4";
import aboutMe from "./images/aboutme.jpg";
import logoNew from "./images/logo-new.jpg";
import roof1Thumb from "./images/roof1-thumb.png";
import roof2Thumb from "./images/roof2-thumb.png";
import roof3Thumb from "./images/roof3-thumb.png";
import roof4Thumb from "./images/roof4-thumb.png";
import roof5Thumb from "./images/roof5-thumb.png";
import roof6Thumb from "./images/roof6-thumb.png";
import roof7Thumb from "./images/roof7-thumb.jpg";
import roofStory2Thumb from "./images/dizaverstory2-thumbnail.jpg";
import roofFullStory1Thumb from "./images/dizaverfullstory1-thumb.jpg";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Macintosh") && "ontouchend" in document)
  );
};

const MainAppContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [enlargedImg, setEnlargedImg] = useState(null);
  const [heroVideoPlaying, setHeroVideoPlaying] = useState(false);

  const yearsExperience = new Date().getFullYear() - 2018;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.state?.scrollToAbout) {
      const scrollToAbout = () => {
        const aboutSection = document.getElementById("about");
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: "smooth" });
        } else {
          setTimeout(scrollToAbout, 100);
        }
      };
      scrollToAbout();
    } else if (location.state?.scrollToSpecials) {
      const scrollToSpecials = () => {
        const specialsSection = document.getElementById("specials");
        if (specialsSection) {
          specialsSection.scrollIntoView({ behavior: "smooth" });
        } else {
          setTimeout(scrollToSpecials, 100);
        }
      };
      scrollToSpecials();
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>
          Dizaver Aluminum - Siding, Repairs & Installation | Toronto Roofing &
          Gutters
        </title>
        <meta
          name="description"
          content="Top-rated Toronto roofing and gutter contractor. Dizaver Aluminum offers roof repairs, gutter cleaning, siding, and custom flashing in Toronto & GTA. Free estimates."
        />
        <meta
          name="keywords"
          content="roofing, siding, gutters, gutter cleaning, roof repair, Toronto, GTA, Dizaver Aluminum, custom flashing, roof inspection, emergency roof repair, roof vent replacement"
        />
        <meta name="author" content="Jose Vera Camacho" />
        {/* Open Graph tags */}
        <meta
          property="og:title"
          content="Dizaver Aluminum - Siding, Repairs & Installation | Toronto Roofing & Gutters"
        />
        <meta
          property="og:description"
          content="Top-rated Toronto roofing and gutter contractor. Dizaver Aluminum offers roof repairs, gutter cleaning, siding, and custom flashing in Toronto & GTA. Free estimates."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dizaveraluminum.ca/" />
        <meta
          property="og:image"
          content="https://dizaveraluminum.ca/logo.png"
        />
        <meta property="og:site_name" content="Dizaver Aluminum" />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Dizaver Aluminum - Siding, Repairs & Installation | Toronto Roofing & Gutters"
        />
        <meta
          name="twitter:description"
          content="Top-rated Toronto roofing and gutter contractor. Dizaver Aluminum offers roof repairs, gutter cleaning, siding, and custom flashing in Toronto & GTA. Free estimates."
        />
        <meta
          name="twitter:image"
          content="https://dizaveraluminum.ca/logo.png"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dizaveraluminum.ca/" />
      </Helmet>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NavBar
          anchorEl={anchorEl}
          handleMenuOpen={handleMenuOpen}
          handleMenuClose={handleMenuClose}
        />
        {/* Hero Text Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #B8860B 0%, #FFD700 100%)",
            color: "#fff",
            py: { xs: 4, md: 6 },
            textAlign: "center",
            mb: 0,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 900,
                fontFamily: '"Bungee", "Montserrat", Arial, sans-serif',
                letterSpacing: 2,
                background: "linear-gradient(135deg, #FFFACD 0%, #FFD700 50%, #FFD700 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textShadow: "none",
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.5rem" },
                lineHeight: 1.1,
                textTransform: "uppercase",
                filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.3))",
              }}
            >
              Roofing and Siding Experts
            </Typography>
            <Typography variant="h5" gutterBottom sx={{
              mb: 2,
              textShadow: "1px 2px 4px rgba(0,0,0,0.2)",
              color: "#fff",
              fontWeight: 500,
            }}>
              Quality Workmanship. Reliable Service. Free Estimates.
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 3,
                fontWeight: 500,
                textShadow: "1px 2px 4px rgba(0,0,0,0.2)",
                color: "#fffaf0",
              }}
            >
              Roofing &nbsp;|&nbsp; Siding &nbsp;|&nbsp; Gutters &nbsp;|&nbsp;
              {yearsExperience}+ Years Experience &nbsp;|&nbsp; Toronto &amp; GTA &nbsp;|&nbsp;
              Free Estimates &nbsp;|&nbsp; Licensed &amp; Insured
            </Typography>
          </Container>
        </Box>

        {/* Hero Video Section */}
        <Box
          sx={{
            position: "relative",
            color: "#fff",
            textAlign: "center",
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            overflow: "hidden",
            mt: { xs: 2, md: 3 },
            mb: { xs: 3, md: 5 },
            aspectRatio: "16 / 9",
            width: "100%",
          }}
        >
          {/* YouTube Video Background */}
          {heroVideoPlaying ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#000",
              }}
            >
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/2juLvi7_2Gg?autoplay=1&rel=0&modestbranding=1&vq=hd1080"
                title="Dizaver Aluminum Siding Installation in Toronto"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
              <IconButton
                onClick={() => setHeroVideoPlaying(false)}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  zIndex: 11,
                  "&:hover": {
                    background: "rgba(0,0,0,0.8)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              component="img"
              src={houseMain}
              alt="Dizaver Team"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                zIndex: 10,
              }}
            />
          )}

          {/* Dark Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3))",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />

          {/* Play Button Overlay */}
          {!heroVideoPlaying && (
            <Box
              onClick={() => {
                setHeroVideoPlaying(true);
              }}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 15,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: "rgba(218, 165, 32, 0.9)",
                transition: "all 0.3s",
                "&:hover": {
                  background: "rgba(218, 165, 32, 1)",
                  transform: "translate(-50%, -50%) scale(1.15)",
                  boxShadow: "0 12px 40px rgba(218, 165, 32, 0.4)",
                },
              }}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: 70, color: "#fff" }} />
            </Box>
          )}
        </Box>

        {/* Special Offer Section */}
        <Box
          id="specials"
          sx={{
            background: "linear-gradient(135deg, #B8860B 0%, #FFD700 100%)",
            color: "#fff",
            py: { xs: 8, md: 12 },
            minHeight: { xs: 300, md: 400 },
            boxShadow: "0 8px 32px rgba(218,165,32,0.25)",
            mb: 6,
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={6}
              alignItems="center"
              justifyContent="center"
            >
              {/* Image on the left */}
              <Grid
                colSpan={{ xs: 12, md: 5 }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(218,165,32,0.18)",
                    border: "4px solid #FFD700",
                    maxWidth: 350,
                    width: "100%",
                    bgcolor: "#fff",
                  }}
                >
                  <img
                    src={gutterClearing}
                    alt="Gutter Cleaning Special"
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                </Box>
              </Grid>
              {/* Text on the right */}
              <Grid
                colSpan={{ xs: 12, md: 7 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: { xs: "center", md: "flex-start" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: "#fff",
                    mb: 2,
                    letterSpacing: 1,
                  }}
                >
                  Gutter Services Special!
                </Typography>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 500, color: "#e3f2fd" }}>
                  Book now and get the best prices for all services.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, color: "#e8f4f8", fontSize: "1.15rem" }}
                >
                  Protect your home from water damage and keep your gutters
                  flowing free.
                  <br />
                  <b>Includes:</b> Gutter Cleaning, New Installation, Custom
                  Flashing and more.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    px: 5,
                    py: 1.75,
                    borderRadius: 12,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    textTransform: "none",
                    background: "#fff",
                    color: "#B8860B",
                    transition:
                      "background 0.3s, box-shadow 0.3s, transform 0.2s",
                    "&:hover": {
                      background: "#f5f5f5",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
                      transform: "translateY(-3px)",
                    },
                  }}
                  href="#about-phone"
                >
                  Call Now for a Free Estimate
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Services Section */}
        <Container id="services" sx={{ py: 8 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              mb: 6,
            }}
          >
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 900,
                letterSpacing: 2,
                color: "#D4A500",
                mb: 2,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3), -1px -1px 2px rgba(255,255,255,0.6)",
                textTransform: "uppercase",
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              Our Services
            </Typography>
            <Box
              sx={{
                width: { xs: "100%", md: 600 },
                maxWidth: "90%",
                height: 5,
                borderRadius: 3,
                background: "linear-gradient(90deg, #FFD700 0%, #FFC107 100%)",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              align="center"
              sx={{
                color: "#666",
                fontWeight: 400,
                fontSize: "1.1rem",
                maxWidth: 600,
              }}
            >
              Professional roofing, siding, and gutter services to protect and enhance your home
            </Typography>
          </Box>
          <Grid
            container
            spacing={4}
            justifyContent="center" // <-- Center cards on all screen sizes
            sx={{
              mt: 2,
              maxWidth: 1120,
              mx: "auto",
            }}
          >
            {/* Emergency Roof Repair */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={emergencyRepair}
                  alt="Emergency Roof Repair"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Emergency Roof Repair
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    For fast, expert, and reliable emergency roof service you
                    can depend on, choose Dizaver Aluminum. We're here to help
                    you through it.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Roof Vent Replacement | Repairs | Installation */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={roofVent}
                  alt="Roof Vent Replacement | Repairs | Installation"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Roof Vent Replacement | Repairs | Installation
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    We replace, repair, and install all types of roof vents to
                    keep your attic cool and your home protected from moisture
                    and heat damage.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Shingles Roof Replacement */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={newRoof}
                  alt="Shingles Roof Replacement"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Shingles Roof Replacement
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    Upgrade your home with high-quality shingles for
                    long-lasting protection and curb appeal. We use trusted
                    brands and expert installation.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Flat Roof Repairs */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={flatRoof}
                  alt="Flat Roof Repairs"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Flat Roof Repairs
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    We specialize in flat roof repairs and maintenance to
                    prevent leaks and extend the life of your roof, using
                    quality materials and proven methods.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Roof Inspection */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={roofInspection}
                  alt="Roof Inspection"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Roof Inspection
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    Get a professional inspection to identify issues before they
                    become costly. We provide honest assessments and clear
                    recommendations.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Roof Repairs */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={roofRepair}
                  alt="Roof Repairs"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Roof Repairs
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    From leaks to missing shingles, we handle all types of roof
                    repairs quickly and efficiently to keep your home safe and
                    dry.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Gutter Cleaning */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={gutterCleaning}
                  alt="Gutter Cleaning"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Gutter Cleaning | Installation
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    Prevent water damage and foundation issues with our thorough
                    gutter cleaning and maintenance services.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            {/* Custom Flashing */}
            <Grid
              colSpan={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }, // Center on mobile, left on desktop
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: 340,
                  height: 370,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: 3,
                  boxShadow: "0 4px 16px rgba(218,165,32,0.12)",
                  border: "none",
                  background: "#fff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0 16px 40px rgba(218,165,32,0.18)",
                  },
                }}
              >
                <img
                  src={siding}
                  alt="Custom Flashing"
                  style={{
                    width: "100%",
                    height: 170,
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: "#FFD700", mb: 1 }}
                  >
                    Custom Flashing
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    We fabricate and install custom flashing to protect
                    vulnerable areas of your roof and walls from water
                    intrusion.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Stats Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #B8860B 0%, #FFD700 100%)",
            color: "#fff",
            py: { xs: 6, md: 8 },
            mb: 10,
            boxShadow: "0 8px 32px rgba(218,165,32,0.25)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 4, md: 8 },
              flexWrap: "wrap",
              width: "100%",
              maxWidth: "1200px",
              px: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ textAlign: "center", flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 auto" } }}>
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                {yearsExperience}+
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Years Experience
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 auto" } }}>
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                500+
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Projects Completed
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 auto" } }}>
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                24/7
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Emergency Service
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Testimonials Section */}
        <Container id="testimonials" sx={{ py: 10, mb: 8 }}>
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h3"
              align="center"
              sx={{
                fontWeight: 900,
                mb: 2,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: "#D4A500",
                textShadow: "2px 2px 4px rgba(0,0,0,0.3), -1px -1px 2px rgba(255,255,255,0.6)",
              }}
            >
              What Our Customers Say
            </Typography>
            <Box sx={{
              width: { xs: "100%", md: 600 },
              maxWidth: "90%",
              height: 5,
              borderRadius: 3,
              background: "linear-gradient(90deg, #FFD700 0%, #FFC107 100%)",
              mx: "auto",
            }} />
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="contained"
                href="https://www.homestars.com/profile/dizaver-aluminum"
                target="_blank"
                rel="noopener"
                sx={{
                  background: "#FFD700",
                  color: "#000",
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  transition: "all 0.3s",
                  "&:hover": {
                    background: "#FFC107",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                  },
                }}
              >
                Read More on HomeStars
              </Button>
            </Box>
          </Box>
          <Grid container spacing={4}>
            <Grid colSpan={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: "#f8f9fa",
                  borderLeft: "6px solid #FFD700",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  animation: `${fadeInUp} 0.6s ease-out`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 36px rgba(218,165,32,0.12)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: "#FFD700", fontSize: "1.3rem" }}>★</span>
                  ))}
                </Box>
                <Typography sx={{ mb: 2, color: "#333", fontStyle: "italic" }}>
                  "Dizaver Aluminum replaced our entire roof and the quality of work was exceptional. Jose and his team were professional, punctual, and cleaned up after themselves. Highly recommend!"
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "#B8860B" }}>
                  Sarah M.
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
                  Toronto, ON
                </Typography>
              </Paper>
            </Grid>
            <Grid colSpan={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: "#f8f9fa",
                  borderLeft: "6px solid #FFD700",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 36px rgba(218,165,32,0.12)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: "#FFD700", fontSize: "1.3rem" }}>★</span>
                  ))}
                </Box>
                <Typography sx={{ mb: 2, color: "#333", fontStyle: "italic" }}>
                  "Best gutter cleaning service in Toronto! They found and fixed issues I didn't even know about. Fast, reliable, and great prices. Will use them again!"
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "#B8860B" }}>
                  John D.
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
                  Mississauga, ON
                </Typography>
              </Paper>
            </Grid>
            <Grid colSpan={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: "#f8f9fa",
                  borderLeft: "6px solid #FFD700",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 36px rgba(218,165,32,0.12)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: "#FFD700", fontSize: "1.3rem" }}>★</span>
                  ))}
                </Box>
                <Typography sx={{ mb: 2, color: "#333", fontStyle: "italic" }}>
                  "Amazing siding installation! The team was courteous and efficient. My home looks brand new. Great communication throughout the entire process."
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "#B8860B" }}>
                  Maria L.
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
                  Brampton, ON
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Instagram Section */}
        <Box
          sx={{
            py: 10,
            background: "#f8f9fa",
            mb: 8,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", mb: 6 }}>
              <Typography
                variant="h3"
                align="center"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 2,
                  color: "#D4A500",
                  mb: 2,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3), -1px -1px 2px rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Follow Us on Instagram
              </Typography>
              <Box
                sx={{
                  width: 120,
                  height: 5,
                  borderRadius: 3,
                  background: "linear-gradient(90deg, #FFD700 0%, #FFC107 100%)",
                  mb: 4,
                }}
              />
              <Typography
                align="center"
                sx={{
                  fontSize: "1.1rem",
                  color: "#666",
                  mb: 6,
                  maxWidth: 600,
                }}
              >
                Check out our latest projects and updates on Instagram @diza_ver
              </Typography>
              <Button
                variant="contained"
                href="https://www.instagram.com/diza_ver/"
                target="_blank"
                rel="noopener"
                sx={{
                  background: "linear-gradient(135deg, #E1306C 0%, #F77737 50%, #FCAF45 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  px: 5,
                  py: 2,
                  borderRadius: 2,
                  transition: "all 0.3s",
                  boxShadow: "0 8px 24px rgba(225,48,108,0.3)",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 36px rgba(225,48,108,0.4)",
                  },
                }}
              >
                Visit Our Instagram
              </Button>
              <Box
                sx={{
                  mt: 6,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <iframe
                  title="Dizaver Aluminum Instagram Feed"
                  src="https://widgets.sociablekit.com/instagram-feed/iframe/25700369"
                  frameBorder="0"
                  width="100%"
                  height="1000px"
                  style={{ maxWidth: "100%", border: "none", borderRadius: "8px" }}
                ></iframe>
              </Box>
            </Box>
          </Container>
        </Box>
        <script src="https://widgets.sociablekit.com/instagram-feed/widget.js" defer></script>

        {/* Gallery Section */}
        <Box
          id="gallery"
          sx={{
            py: 10,
            background: "#f8f9fa",
            mb: 8,
          }}
        >
          <Container>
            <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", mb: 6 }}>
              <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 900,
                  letterSpacing: 2,
                  color: "#D4A500",
                  mb: 2,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3), -1px -1px 2px rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Gallery
              </Typography>
              <Box
                sx={{
                  width: 120,
                  height: 5,
                  borderRadius: 3,
                  background: "linear-gradient(90deg, #FFD700 0%, #FFC107 100%)",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: "1200px" }}>
            {[
              {
                src: roofFullStory1,
                alt: "Gallery 9",
                type: "video",
                poster: roofFullStory1Thumb,
              },
              {
                src: roofStory1,
                alt: "Gallery 8",
                type: "video",
                poster: roofStory2Thumb,
              },
              {
                src: roof7Video,
                alt: "Gallery 7",
                type: "video",
                poster: roof7Thumb,
              },
              {
                src: roof1Video,
                alt: "Gallery 6",
                type: "video",
                poster: roof1Thumb,
              },
              {
                src: roof2Video,
                alt: "Gallery 5",
                type: "video",
                poster: roof2Thumb,
              },
              {
                src: roof3Video,
                alt: "Gallery 4",
                type: "video",
                poster: roof3Thumb,
              },
              {
                src: roof4Video,
                alt: "Gallery 3",
                type: "video",
                poster: roof4Thumb,
              },
              {
                src: roof5Video,
                alt: "Gallery 2",
                type: "video",
                poster: roof5Thumb,
              },
              {
                src: roof6Video,
                alt: "Gallery 1",
                type: "video",
                poster: roof6Thumb,
              },
            ].map((img, idx) => (
              <Grid
                colSpan={{ xs: 12, sm: 6, md: 4 }}
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: 3,
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    width: 320,
                    height: 200,
                    background: "#eee",
                    position: "relative",
                    "&:hover": { transform: "scale(1.03)" },
                  }}
                  onClick={() => setEnlargedImg(img)}
                >
                  {img.type === "video" ? (
                    <>
                      {isIOS() ? (
                        <img
                          src={img.poster || "/video-thumb.jpg"}
                          alt={img.alt}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <video
                          src={img.src}
                          poster={img.poster || "/video-thumb.jpg"}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        />
                      )}
                      {/* Video icon overlay */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "rgba(255,255,255,0.85)",
                          bgcolor: "rgba(0,0,0,0.35)",
                          borderRadius: "50%",
                          p: 0.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <PlayCircleOutlineIcon sx={{ fontSize: 56 }} />
                      </Box>
                    </>
                  ) : (
                    <img
                      src={img.src}
                      alt={img.alt}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
              </Grid>
            </Box>
          {/* Enlarged image/video modal */}
          {enlargedImg && (
            <Box
              onClick={() => setEnlargedImg(null)}
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "rgba(0,0,0,0.75)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300,
                cursor: "zoom-out",
              }}
            >
              {enlargedImg.type === "video" ? (
                <video
                  src={enlargedImg.src}
                  controls
                  autoPlay // <-- This ensures the video starts playing automatically
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 8,
                    boxShadow: "0 8px 32px rgba(218,165,32,0.18)",
                    background: "#000",
                  }}
                />
              ) : (
                <img
                  src={enlargedImg.src}
                  alt={enlargedImg.alt}
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 8,
                    boxShadow: "0 8px 32px rgba(218,165,32,0.18)",
                    transition: "transform 0.3s",
                  }}
                />
              )}
            </Box>
          )}
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #B8860B 0%, #FFD700 100%)",
            color: "#fff",
            py: { xs: 8, md: 10 },
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(218,165,32,0.25)",
            my: 8,
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Ready to Upgrade Your Home?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                fontSize: "1.2rem",
                fontWeight: 400,
                color: "#fffaf0",
              }}
            >
              Get a free estimate from our expert team today. No obligation, no pressure.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  px: 5,
                  py: 2,
                  borderRadius: 12,
                  background: "#fff",
                  color: "#B8860B",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                  transition: "all 0.3s",
                  "&:hover": {
                    background: "#f5f5f5",
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
                  },
                }}
                href="tel:4378557047"
              >
                Call (437) 855-7047
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  px: 5,
                  py: 2,
                  borderRadius: 12,
                  borderColor: "#fff",
                  color: "#fff",
                  transition: "all 0.3s",
                  "&:hover": {
                    background: "rgba(255,255,255,0.1)",
                    borderColor: "#fff",
                    transform: "translateY(-3px)",
                  },
                }}
                href="#testimonials"
              >
                See Reviews
              </Button>
            </Box>
          </Container>
        </Box>

        {/* About Section */}
        <Box
          sx={{
            background: "#fff",
            py: { xs: 8, md: 12 },
            pb: { xs: 4, md: 6 },
            boxShadow: "inset 0 2px 12px rgba(218,165,32,0.05)",
            position: "relative",
            overflow: "hidden",
          }}
          id="about"
        >
          {/* Decorative background shapes */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              left: -60,
              width: 180,
              height: 180,
              bgcolor: "#bdbdbd", // silver
              opacity: 0.08,
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -150,
              right: -150,
              width: 220,
              height: 220,
              bgcolor: "#bdbdbd", // silver
              opacity: 0.1,
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Box
                component="img"
                src={logoNew}
                alt="Dizaver Aluminum"
                sx={{
                  height: { xs: 140, md: 180 },
                  width: "auto",
                  borderRadius: 2,
                  boxShadow: "0 8px 24px rgba(218,165,32,0.2)",
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              />
            </Box>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 900,
                letterSpacing: 1,
                color: "#D4A500",
                textShadow: "2px 2px 4px rgba(0,0,0,0.3), -1px -1px 2px rgba(255,255,255,0.6)",
                mb: 4,
                textTransform: "uppercase",
                position: "relative",
                display: "inline-block",
                px: 3,
                left: "50%",
                transform: "translateX(-50%)",
                "&:after": {
                  content: '""',
                  display: "block",
                  width: 200,
                  height: 4,
                  borderRadius: 2,
                  background:
                    "linear-gradient(90deg, #FFD700 0%, #FFC107 100%)",
                  margin: "16px auto 0 auto",
                },
              }}
            >
              About Us
            </Typography>

            <Typography
              variant="h5"
              align="center"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #FFFACD 0%, #FFD700 50%, #FFD700 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 3,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Testimonials
            </Typography>
            <Typography
              align="center"
              sx={{
                fontSize: "1rem",
                color: "#757575",
                mb: 2,
                fontStyle: "italic",
              }}
            >
              Reviews sourced from{" "}
              <a
                href="https://www.homestars.com/profile/dizaver-aluminum"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#FFD700", textDecoration: "underline" }}
              >
                homestars.com
              </a>
            </Typography>
            <Grid container spacing={2} direction="column" alignItems="center">
              {[
                {
                  text: "Install siding or stucco: Repair; PVC siding\nAbsolutely thrilled with the siding job—5 out of 5 stars! Jose was awesome from start to finish. He showed up on time, worked efficiently, and paid attention to every detail. The quality of workmanship is top-notch, and the house looks brand new. Jose was professional, friendly, and kept everything clean throughout the project. He also took the time to answer all my questions and made sure I was satisfied with every step. It's rare to find someone who takes so much pride in their work. I highly recommend Jose for any siding work—he exceeded all expectations!",
                  author: "Moe, Brampton",
                },
                {
                  text: "Repair or maintain roof or gutter: Need metal flashing around 3 existing windows.\nJob was well done and Jose was very prompt, professional and urgent.",
                  author: "Chris, Etobicoke",
                },
                {
                  text: "Install siding or stucco: Reface garage front with vinyl siding; Aluminum siding. Mr. Riva and the team at Dizaver Aluminum did an outstanding job refurbishing my garage with aluminum siding. The work was clean, precise, and completed on time. Mr. Riva was professional, helpful, and a pleasure to work with. Highly recommend for anyone looking for quality craftsmanship and reliable service!",
                  author: "Jack, Toronto",
                },
                {
                  text: `Install siding or stucco: Replacement; PVC siding
Great guys to work with, very accommodating and did what you asked. Great price and I recommend them`,
                  author: "HomeStars User, Toronto",
                },
                {
                  text: `Repair or maintain roof or gutter: Repairs; Gutters
Two Men and a truck fast thorough and complete great job highly recommend thank you boys`,
                  author: "Spencer, Caledonia",
                },
                {
                  text: `Repair or treat facade: Metal Siding
Was very quick to respond. Finished the work in one day`,
                  author: "HomeStars user, Scarborough",
                },
              ].map((testimonial, idx) => (
                <Grid
                  colSpan={{ xs: 12 }}
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 4, // More spacing after each review
                  }}
                >
                  <Paper
                    elevation={4}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: "#f9fbe7",
                      width: { xs: "100%", sm: 700, md: 800 },
                      minHeight: 180,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      textAlign: "left",
                      transition:
                        "box-shadow 0.3s, background 0.3s, transform 0.25s",
                      boxShadow: "0 2px 8px rgba(218,165,32,0.08)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 32px 0 rgba(218,165,32,0.18), 0 2px 16px 0 rgba(66,165,245,0.10)",
                        background:
                          "linear-gradient(120deg, #f9fbe7 80%, #e3f2fd 100%)",
                        transform: "scale(1.03)",
                      },
                    }}
                  >
                    <Box
                      sx={{ color: "#FFD700", mb: 1, alignSelf: "flex-start" }}
                    >
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          style={{ fontSize: 28, verticalAlign: "middle" }} // Increased from 20 to 28
                        >
                          ★
                        </span>
                      ))}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: "italic",
                        mb: 1,
                        whiteSpace: "pre-line",
                        textAlign: "left",
                      }}
                    >
                      "{testimonial.text}"
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#757575",
                        textAlign: "left",
                      }}
                    >
                      – {testimonial.author}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6, mb: 6 }}>
              <Button
                variant="contained"
                href="https://www.homestars.com/profile/dizaver-aluminum"
                target="_blank"
                rel="noopener"
                sx={{
                  background: "#FFD700",
                  color: "#000",
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  transition: "all 0.3s",
                  "&:hover": {
                    background: "#FFC107",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                  },
                }}
              >
                View All Reviews on HomeStars
              </Button>
            </Box>

            <Grid
              container
              spacing={6}
              alignItems="center"
              justifyContent="center"
              sx={{
                background: "#fff",
                borderRadius: 4,
                boxShadow: "0 4px 24px rgba(218,165,32,0.10)",
                px: { xs: 2, md: 6 },
                py: { xs: 3, md: 5 },
                flexDirection: { xs: "column", md: "row" },
                width: "100%",
                margin: "0 auto",
              }}
            >
              <Grid
                colSpan={{ xs: 12, md: 5 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  width: "100%",
                  margin: "0 auto",
                }}
              >
                <Typography
                  variant="h6"
                  align="center"
                  sx={{
                    fontWeight: 700,
                    color: "#D4A500",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.3), -1px -1px 2px rgba(255,255,255,0.6)",
                    mb: 2,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    width: "100%",
                    fontSize: { xs: "1.6rem", md: "2rem" }, // Slightly larger than Jose
                  }}
                >
                  My Story
                </Typography>
                <Box
                  sx={{
                    width: { xs: "100%", md: 300 },
                    maxWidth: "90%",
                    height: 5,
                    borderRadius: 3,
                    background: "linear-gradient(90deg, #FFD700 0%, #FFC107 100%)",
                    mb: 3,
                  }}
                />
                <Box
                  component="img"
                  src={aboutMe}
                  alt="Jose Vera Camacho"
                  sx={{
                    width: { xs: 240, md: 320 },
                    height: { xs: 240, md: 320 },
                    borderRadius: 4,
                    objectFit: "cover",
                    boxShadow: "0 8px 32px rgba(218,165,32,0.18)",
                    transition: "transform 0.35s cubic-bezier(.4,2,.6,1)",
                    border: "4px solid #e3f2fd",
                    background: "#fff",
                    p: 0,
                    "&:hover": {
                      transform: "scale(1.07)",
                      boxShadow: "0 16px 48px rgba(218,165,32,0.22)",
                      borderColor: "#FFD700",
                    },
                  }}
                />
                <Typography
                  variant="subtitle1"
                  align="center"
                  sx={{
                    mt: 2,
                    fontWeight: 700,
                    color: "#B8860B",
                    letterSpacing: 2,
                    fontSize: { xs: "1.3rem", md: "1.5rem" }, // Slightly smaller than My Story
                    fontFamily: '"Montserrat", "Bungee", Arial, sans-serif',
                    textShadow: "0 2px 8px rgba(218,165,32,0.10)",
                  }}
                >
                  Jose
                </Typography>
              </Grid>
              <Grid colSpan={{ xs: 12, md: 7 }}>
                <Typography
                  align="left"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "1.08rem", md: "1.18rem" },
                    color: "#333",
                    lineHeight: 1.7,
                    fontWeight: 400,
                    textShadow: "0 1px 4px rgba(218,165,32,0.04)",
                  }}
                >
                  I am a responsible and dedicated professional who always
                  brings a positive attitude and honesty to every project. My
                  journey began over six years ago, starting as a garbage
                  collector and working my way up to foreman at a roofing
                  company. Through hard work and a passion for learning, I
                  gained hands-on experience in roofing, flat roofs, siding,
                  aluminum, and more.
                  <br />
                  <br />
                  In 2022, I started my own company, committed to delivering the
                  best customer service and quality workmanship. I take pride in
                  every job, no matter the size, and strive to exceed my
                  clients’ expectations. If you choose to work with me, you can
                  expect dedication, reliability, and results you’ll be happy
                  with!
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "#D4A500",
                        mb: 0.5,
                      }}
                    >
                      Contact: Jose Vera Camacho
                    </Typography>
                    <Typography
                      id="about-phone"
                      sx={{ fontSize: "1.1rem", color: "#333", mb: 0.5 }}
                    >
                      Phone:{" "}
                      <a
                        href="tel:4378557047"
                        style={{
                          color: "#D4A500",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        (437) 855-7047
                      </a>
                    </Typography>
                    <Typography
                      sx={{ fontSize: "1.1rem", color: "#333", mb: 1 }}
                    >
                      Email:{" "}
                      <a
                        href="mailto:dizaver.aluminum@outlook.com"
                        style={{
                          color: "#D4A500",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        dizaver.aluminum@outlook.com
                      </a>
                    </Typography>
                    <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
                      <IconButton
                        href="https://www.facebook.com/profile.php?id=61577040364013"
                        target="_blank"
                        rel="noopener"
                        aria-label="Facebook"
                        sx={{
                          color: "#D4A500",
                          background: "transparent",
                          "&:hover": { background: "rgba(212,165,0,0.1)" },
                          boxShadow: "none",
                        }}
                      >
                        <FacebookIcon />
                      </IconButton>
                      <IconButton
                        href="https://www.instagram.com/diza_ver/"
                        target="_blank"
                        rel="noopener"
                        aria-label="Instagram"
                        sx={{
                          color: "#D4A500",
                          background: "transparent",
                          "&:hover": { background: "rgba(212,165,0,0.1)" },
                          boxShadow: "none",
                        }}
                      >
                        <InstagramIcon />
                      </IconButton>
                      <IconButton
                        href="https://www.youtube.com/@DizaverAluminum"
                        target="_blank"
                        rel="noopener"
                        aria-label="YouTube"
                        sx={{
                          color: "#D4A500",
                          background: "transparent",
                          "&:hover": { background: "rgba(212,165,0,0.1)" },
                          boxShadow: "none",
                        }}
                      >
                        <YouTubeIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* Address and Google Map */}
                  <Box sx={{ minWidth: 220, maxWidth: 300 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "#D4A500",
                        mb: 0.5,
                      }}
                    >
                      Address
                    </Typography>
                    <Typography
                      sx={{ fontSize: "1.05rem", color: "#333", mb: 1 }}
                    >
                      63 Westmount Ave.
                      <br />
                      Toronto, ON M6H 3K2
                    </Typography>
                    {!isMobile && (
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: 2,
                        }}
                      >
                        <iframe
                          key="desktop"
                          title="Google Map"
                          width="100%"
                          height="160"
                          frameBorder="0"
                          style={{ border: 0, width: "100%", minWidth: 200 }}
                          src="https://www.google.com/maps?q=63+Westmount+Ave,+Toronto,+ON+M6H+3K2&output=embed"
                          allowFullScreen
                        ></iframe>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Footer />
      </ThemeProvider>
    </>
  );
};

export default MainAppContent;
